// src/components/SignalHistory.js
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaTrash, FaTrashAlt, FaSync, FaDownload } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = 'https://gritwears.store/api';

const SignalHistory = () => {
    const { user } = useSelector((state) => state.auth);
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [symbolFilter, setSymbolFilter] = useState('all');

    const loadSignals = async () => {
        if (!user?.uid) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/signals/log/${user.uid}`);
            setSignals(response.data);
        } catch (error) {
            console.error('Error loading signals:', error);
            toast.error('Failed to load signal history');
        } finally {
            setLoading(false);
        }
    };

    const deleteSignal = async (signalId) => {
        if (!window.confirm('Delete this signal from history?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/signals/log/${user.uid}/${signalId}`);
            await loadSignals();
            toast.success('Signal deleted');
        } catch (error) {
            toast.error('Failed to delete signal');
        }
    };

    const clearAllSignals = async () => {
        if (!window.confirm('Delete ALL signal history? This cannot be undone.')) return;
        try {
            await axios.delete(`${API_BASE_URL}/signals/log/${user.uid}`);
            await loadSignals();
            toast.success('All signals cleared');
        } catch (error) {
            toast.error('Failed to clear signals');
        }
    };

    const exportSignals = () => {
        const dataStr = JSON.stringify(signals, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', `tradehawk_signals_${new Date().toISOString()}.json`);
        linkElement.click();
        toast.success('Signals exported');
    };

    useEffect(() => {
        loadSignals();
    }, [user]);

    const getUniqueSymbols = () => ['all', ...new Set(signals.map(s => s.symbol))];

    const filteredSignals = signals.filter(signal => {
        if (filter !== 'all' && signal.type !== filter) return false;
        if (symbolFilter !== 'all' && signal.symbol !== symbolFilter) return false;
        return true;
    });

    const getPatternIcon = (type) => {
        if (type === 'CHoCH') return '🔄';
        if (type === 'CISD') return '⚡';
        if (type === 'BOS') return '📊';
        return '📈';
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-header"><h2>📋 Signal History</h2><button className="btn btn-secondary btn-small" disabled><FaSync className="spinning" /> Loading...</button></div>
                <div className="loading-spinner-container"><div className="spinner-small"></div><p>Loading signal history...</p></div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2>📋 Signal History ({filteredSignals.length})</h2>
                <div className="signal-actions">
                    <div className="signal-filters">
                        <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
                        <button className={`filter-btn ${filter === 'CHoCH' ? 'active' : ''}`} onClick={() => setFilter('CHoCH')}>🔄 CHoCH</button>
                        <button className={`filter-btn ${filter === 'CISD' ? 'active' : ''}`} onClick={() => setFilter('CISD')}>⚡ CISD</button>
                        <button className={`filter-btn ${filter === 'BOS' ? 'active' : ''}`} onClick={() => setFilter('BOS')}>📊 BOS</button>
                    </div>
                    <select value={symbolFilter} onChange={(e) => setSymbolFilter(e.target.value)} className="symbol-filter">
                        {getUniqueSymbols().map(s => <option key={s} value={s}>{s === 'all' ? 'All Symbols' : s}</option>)}
                    </select>
                    <div className="signal-buttons">
                        <button onClick={loadSignals} className="btn btn-secondary btn-small"><FaSync /> Refresh</button>
                        <button onClick={exportSignals} className="btn btn-secondary btn-small"><FaDownload /> Export</button>
                        <button onClick={clearAllSignals} className="btn btn-danger btn-small"><FaTrashAlt /> Clear All</button>
                    </div>
                </div>
            </div>
            
            {filteredSignals.length === 0 ? (
                <div className="empty-state"><p className="text-muted">No signals detected yet</p><p className="text-muted-small">Signals will appear here when CHoCH, CISD, or BOS patterns are detected</p></div>
            ) : (
                <div className="signal-history-list">
                    {filteredSignals.map((signal) => (
                        <div key={signal.id} className={`signal-history-item ${signal.type.toLowerCase()}-${signal.direction}`}>
                            <div className="signal-history-header">
                                <div className="signal-history-type">
                                    <span className="signal-icon">{getPatternIcon(signal.type)}</span>
                                    <span className="signal-name">{signal.type}</span>
                                    <span className={`signal-direction ${signal.direction}`}>{signal.direction === 'bullish' ? '🟢 BUY' : '🔴 SELL'}</span>
                                </div>
                                <button onClick={() => deleteSignal(signal.id)} className="delete-signal-btn" title="Delete signal"><FaTrash /></button>
                            </div>
                            <div className="signal-history-details">
                                <div className="signal-detail"><span className="detail-label">Symbol:</span><span className="detail-value">{signal.symbol}</span></div>
                                <div className="signal-detail"><span className="detail-label">Timeframe:</span><span className="detail-value">{signal.interval}s</span></div>
                                <div className="signal-detail"><span className="detail-label">Price:</span><span className="detail-value">{signal.price?.toFixed(5)}</span></div>
                                <div className="signal-detail"><span className="detail-label">Time:</span><span className="detail-value">{signal.readableTime || new Date(signal.timestamp).toLocaleString()}</span></div>
                            </div>
                            {signal.reason && <div className="signal-history-reason">{signal.reason.split('\n')[0]}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SignalHistory;